import { Link } from "@mui/material";
import { FC, useCallback, useMemo, useState } from "react";
import { useDispatch } from "react-redux";

import __ from "web/Layout/Translations";

import Dialog from "web/Components/mui/Dialog";

import { ErrorData, IApiRequestMethods } from "web/api/apiRequestTypes";

import getErrorInfo from "web/utils/system/essentials/getErrorInfo";
import newRelicErrorReport from "web/utils/system/essentials/newRelicErrorReport";
import BrowserPersistence from "web/utils/system/storage/storage/browserPersistence";

import storageNames from "web/constants/storageNames";

import { ISubscriptionReceiver } from "web/types/Subscription";
import { Nullable } from "web/types/Utils";

import { request } from "web/api";
import { useAppContext } from "web/context/app";
import {
  setNotificationError,
  setNotificationSuccess,
} from "web/features/app/appSlice";

import EditEmailMUI from "../../EditEmail/editEmailMUI";
import ActivityItemMUI, { ActivityItemProps } from "../activityItemMUI";

type BenefitUUID = string;

interface IEmailSenderActivityItemProps extends ActivityItemProps {
  benefitUUID: BenefitUUID;
  receiver: ISubscriptionReceiver;
  resendLink: string;
}

const modalTitle =
  "Edytuj adres e-mail, na który chcesz ponownie wysłać formularz.";

const storage = new BrowserPersistence();

const EmailSenderActivityItemMUI: FC<IEmailSenderActivityItemProps> = ({
  benefitUUID,
  receiver,
  resendLink,
  name,
  description,
}) => {
  const { nextSendAfter } = receiver?.form || {};
  const [dialogOpen, setDialogOpen] = useState(false);
  // Initial state for button activity, base on nextSenfAfter date
  const buttonShouldBeActive = useMemo(() => {
    return nextSendAfter
      ? new Date(nextSendAfter).getTime() < new Date().getTime()
      : true;
  }, [nextSendAfter]);

  // Preparation, at what time is it possible to send another request
  const prepNextSendAfter = useMemo(() => {
    return nextSendAfter ? new Date(nextSendAfter) : null;
  }, [nextSendAfter]);

  const timeFromNextSendAfter =
    prepNextSendAfter &&
    prepNextSendAfter.toLocaleTimeString("en-GB", { hour12: false });

  const dispatch = useDispatch();
  const [isEmailButtonActive, setIsEmailButtonActive] =
    useState(buttonShouldBeActive);
  const [blockedButtonText, setBlockedButtonText] =
    useState<Nullable<string>>(null);

  const { isMobile } = useAppContext();

  const editEmailButton = (
    <Link
      component="button"
      onClick={() => setDialogOpen(true)}
      sx={{
        textDecoration: "underline",
        textDecorationColor: "rgba(33, 150, 243, 0.4)",
        textDecorationSkipInk: "none",
        mt: 1,
        fontWeight: 400,
      }}
    >
      {__("Edytuj adres e-mail")}
    </Link>
  );

  const onResendEmailHandler = useCallback(async () => {
    const token = storage.getItem(storageNames.tokenAccess);

    try {
      const response = (await request(resendLink, {
        headers: {
          authorization: `Bearer ${token}`,
        },
        method: IApiRequestMethods.POST,
        body: JSON.stringify({}),
        parseJSON: false,
      })) as Response;

      const responseData = await response.json();
      const { date } = responseData?.nextAvailableDate || {};

      const buttonShouldBeActive = date
        ? new Date(date).getTime() < new Date().getTime()
        : true;

      if (!buttonShouldBeActive && response.ok) {
        const timeFromNextSendAfter = new Date(date).toLocaleTimeString(
          "en-GB",
          { hour12: false }
        );
        const btnText = `${__(
          "Ponowne wysłanie możliwe o"
        )} ${timeFromNextSendAfter}`;

        setBlockedButtonText(btnText);
        setIsEmailButtonActive(false);
      }

      if (buttonShouldBeActive && response.ok)
        dispatch(
          setNotificationSuccess({ message: "Pomyślnie wysłano e-mail" })
        );
    } catch (errorData) {
      if ((errorData as ErrorData)?.response?.status === 425) {
        const additionalInfo = getErrorInfo(errorData as ErrorData);
        const blockadeDate = new Date(additionalInfo.nextAvailableDate.date);
        const unlockTime = new Date(blockadeDate.getTime())
          .toLocaleString()
          .substr(12);
        const btnText = `${__("Ponowne wysłanie możliwe o")} ${unlockTime}`;
        setBlockedButtonText(btnText);
        setIsEmailButtonActive(false);

        const nextDateMessage = `${__(
          "Ponowne wysłanie e-maila będzie możliwe o"
        )} ${unlockTime}`;
        dispatch(setNotificationError({ message: nextDateMessage }));
      } else {
        dispatch(
          setNotificationError({
            message: __(
              "Nie udało się wysłać maila, spróbuj ponownie później."
            ),
          })
        );
      }

      newRelicErrorReport(
        errorData,
        "web-app/web/Components/Common/SubscriptionActivities/ActivityItem/EmailSenderItem/emailSenderItem.tsx - 79"
      );
    }
  }, [resendLink, dispatch, setNotificationError]);

  const content = useMemo(() => {
    if (blockedButtonText) {
      return blockedButtonText;
    }
    if (isEmailButtonActive) {
      return __("Wyślij ponownie");
    }
    return __(`${__("Ponowne wysłanie możliwe o")} ${timeFromNextSendAfter}`);
  }, [blockedButtonText, isEmailButtonActive, timeFromNextSendAfter]);

  return (
    <>
      <ActivityItemMUI
        name={name}
        additionalElement={editEmailButton}
        onClick={onResendEmailHandler}
        disabled={!isEmailButtonActive}
        description={description}
      >
        {content}
      </ActivityItemMUI>
      <Dialog
        sx={{
          "& .MuiDialog-paper": {
            minWidth: isMobile ? 0 : "612px",
          },
          ...(!isMobile && {
            ".MuiDialogContent-root, & .MuiDialogTitle-root": {
              p: 0,
            },
            ".MuiDialogTitle-root": {
              pb: 2,
            },
            ".MuiPaper-root": {
              p: 3,
            },
          }),
        }}
        fullScreen={isMobile}
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        title={__(modalTitle)}
        noTitleDivider
      >
        {/** TODO Krok2: Zaimplementowanie RWD do zagnieżdzonych komponentów */}
        <EditEmailMUI
          receiver={receiver}
          benefitUUID={benefitUUID}
          closeModal={() => setDialogOpen(false)}
          isMobile={isMobile}
        />
      </Dialog>
    </>
  );
};

export default EmailSenderActivityItemMUI;
